import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';

import { getStripeConnectStatus, recreateMyStripeConnectAccount } from '../../Services/marketplacePayments';
import StripeEmbeddedComponent from './StripeEmbeddedComponent';
import ArtisanMoneyDashboard from './ArtisanMoneyDashboard';
import { resetStripeConnectInstance } from '../../Services/stripeConnect';
import { useI18n } from '../../i18n/I18nProvider';

const friendlyError = (error, en = false) => {
  const message = String(error?.message || '').replace(/^Firebase:\s*/i, '').trim();
  const code = String(error?.code || '').toLowerCase();
  if (message && !/^internal$/i.test(message) && !message.toLowerCase().includes('functions/internal')) return message;
  if (code.includes('unauthenticated')) return en ? 'Sign in again before configuring payments.' : 'Reconnectez-vous avant de configurer vos paiements.';
  if (code.includes('permission-denied')) return en ? 'This action is reserved for artisan accounts.' : 'Cette action est réservée aux comptes artisans.';
  if (code.includes('failed-precondition')) return en ? 'The payment service setup must be completed before continuing.' : 'La configuration du service de paiement doit être finalisée avant de continuer.';
  return en ? 'Your payment space could not be loaded. Try again shortly.' : 'Votre espace de paiement n’a pas pu être chargé. Réessayez dans quelques instants.';
};

const paymentViews = (en) => [
  { id: 'account-management', label: en ? 'Identity and IBAN' : 'Identité et IBAN', icon: BadgeOutlinedIcon },
  { id: 'payouts', label: en ? 'Transfers' : 'Versements', icon: AccountBalanceOutlinedIcon },
  { id: 'payments', label: en ? 'Payments received' : 'Paiements reçus', icon: PaymentsOutlinedIcon },
  { id: 'documents', label: en ? 'Payment documents' : 'Documents de paiement', icon: ReceiptLongOutlinedIcon },
];

const StripeConnectPanel = () => {
  const { language } = useI18n();
  const en = language === 'en';
  const L = (fr, english) => (en ? english : fr);
  const views = paymentViews(en);
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [error, setError] = useState('');
  const [activeView, setActiveView] = useState('');
  const [resettingAccount, setResettingAccount] = useState(false);

  const refresh = useCallback(async () => {
    setLoadingStatus(true);
    setError('');
    try {
      setStatus(await getStripeConnectStatus());
    } catch (nextError) {
      setError(friendlyError(nextError, en));
    } finally {
      setLoadingStatus(false);
    }
  }, [en]);

  useEffect(() => {
    refresh();
    return () => resetStripeConnectInstance();
  }, [refresh]);

  const ready = Boolean(status?.ready);
  const started = Boolean(status?.account_id);
  const pricingIncompatible = started && status?.pricing_compatible === false;
  const pendingCount = useMemo(() => (
    (status?.requirements?.length || 0)
    + (status?.pending_verification?.length || 0)
    + (status?.future_requirements?.length || 0)
  ), [status]);

  const stateLabel = ready ? L('Prêt à recevoir', 'Ready to receive') : pricingIncompatible ? L('Mise à niveau requise', 'Upgrade required') : started ? L('À terminer', 'To complete') : L('Non activé', 'Not activated');
  const embeddedComponent = ready ? (activeView || '') : (activeView === 'onboarding' ? 'account-onboarding' : '');

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.dispatchEvent(new CustomEvent('lpb:stripe-embedded-state', {
      detail: { open: Boolean(embeddedComponent), source: 'stripe' },
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('lpb:stripe-embedded-state', {
        detail: { open: false, source: 'stripe' },
      }));
    };
  }, [embeddedComponent]);

  const closeEmbedded = async () => {
    await refresh();
    setActiveView('');
  };

  const recreateAccount = async () => {
    setResettingAccount(true);
    setError('');
    try {
      await recreateMyStripeConnectAccount();
      resetStripeConnectInstance();
      setStatus(null);
      setActiveView('onboarding');
    } catch (nextError) {
      setError(friendlyError(nextError, en));
    } finally {
      setResettingAccount(false);
    }
  };

  return (
    <Card $expanded={Boolean(embeddedComponent)}>
      <Header>
        <Icon><AccountBalanceOutlinedIcon /></Icon>
        <div>
          <h3>{L("Mes paiements et mon IBAN", "My payments and IBAN")}</h3>
          <p>
            {L("Tout se fait ici dans LePetitBoulot. Notre prestataire de paiement vérifie votre identité et conserve vos données bancaires de façon sécurisée.", "Everything happens here in LePetitBoulot. Our payment provider verifies your identity and securely stores your banking details.")}
          </p>
        </div>
        <State $ready={ready}>{ready && <VerifiedOutlinedIcon />} {stateLabel}</State>
      </Header>

      {!embeddedComponent && (
        <>
          <Steps aria-label={L("Étapes d’activation des paiements", "Payment activation steps")}>
            <Step $done={Boolean(status?.details_submitted || ready)}>
              <BadgeOutlinedIcon />
              <div><strong>{L("1. Identité", "1. Identity")}</strong><span>{L("Informations professionnelles et justificatif.", "Business information and identity document.")}</span></div>
            </Step>
            <Step $done={Boolean(status?.payouts_enabled || ready)}>
              <PaymentsOutlinedIcon />
              <div><strong>{L("2. Compte bancaire", "2. Bank account")}</strong><span>{L("IBAN utilisé pour recevoir vos versements.", "IBAN used to receive bank transfers.")}</span></div>
            </Step>
            <Step $done={ready}>
              <SecurityOutlinedIcon />
              <div><strong>{L("3. Activation", "3. Activation")}</strong><span>{L("Le prestataire de paiement valide le compte avant le premier règlement.", "The payment provider validates the account before the first payment.")}</span></div>
            </Step>
          </Steps>

          {ready ? (
            <SuccessMessage><VerifiedOutlinedIcon /> {L("Votre espace de paiement est actif. Les clients peuvent accepter vos devis et payer dans LePetitBoulot.", "Your payment space is active. Clients can accept your quotes and pay in LePetitBoulot.")}</SuccessMessage>
          ) : (
            <Info>
              {L("Prévoyez une pièce d’identité et votre IBAN. Vous pourrez interrompre la procédure et la reprendre plus tard.", "Have an identity document and your IBAN ready. You can pause the process and continue later.")}
              {pendingCount > 0 ? L(` ${pendingCount} élément${pendingCount > 1 ? 's sont' : ' est'} encore à compléter ou en vérification.`, ` ${pendingCount} item${pendingCount > 1 ? 's are' : ' is'} still incomplete or being reviewed.`) : ''}
            </Info>
          )}
        </>
      )}

      {pricingIncompatible && !embeddedComponent && (
        <UpgradeNotice role="status">
          <div>
            <strong>{L("Votre ancien espace de paiement doit être mis à niveau.", "Your previous payment space must be upgraded.")}</strong>
            <span>
              {L("Le nouveau modèle sépare clairement le prix du chantier, les services Le Petit Boulot et les frais de traitement bancaire. Cette opération est disponible uniquement lorsqu’aucun chantier accepté ni paiement n’est lié à cet espace.", "The new model clearly separates the job price, Le Petit Boulot services and bank processing fees. This operation is available only when no accepted job or payment is linked to this space.")}
            </span>
          </div>
          <UpgradeButton type="button" onClick={recreateAccount} disabled={resettingAccount || loadingStatus}>
            <RestartAltOutlinedIcon /> {resettingAccount ? L('Préparation…', 'Preparing…') : L('Mettre à niveau mon espace', 'Upgrade my payment space')}
          </UpgradeButton>
        </UpgradeNotice>
      )}

      {!ready && !pricingIncompatible && !embeddedComponent && (
        <PrimaryButton type="button" onClick={() => setActiveView('onboarding')} disabled={loadingStatus}>
          {started ? L('Reprendre la configuration', 'Continue setup') : L('Configurer mes paiements', 'Set up payments')}
        </PrimaryButton>
      )}

      {ready && !embeddedComponent && (
        <ArtisanMoneyDashboard onOpenPayouts={() => setActiveView('payouts')} />
      )}

      {ready && !embeddedComponent && (
        <Menu>
          {views.map(({ id, label, icon: ViewIcon }) => (
            <MenuButton key={id} type="button" onClick={() => setActiveView(id)}>
              <ViewIcon /> <span>{label}</span>
            </MenuButton>
          ))}
        </Menu>
      )}

      {embeddedComponent && (
        <EmbeddedArea>
          <EmbeddedHeader>
            <div>
              <strong>{embeddedComponent === 'account-onboarding' ? L('Activation sécurisée', 'Secure activation') : views.find((item) => item.id === embeddedComponent)?.label}</strong>
              <span>{L("Vous restez dans Le Petit Boulot. Le module bancaire ci-dessous est fourni directement par notre prestataire de paiement afin de protéger vos informations sensibles.", "You stay inside Le Petit Boulot. The secure banking module below is provided directly by our payment provider to protect sensitive information.")}</span>
            </div>
            <CloseButton type="button" onClick={closeEmbedded}><ArrowBackOutlinedIcon /> {L("Retour au tableau de bord", "Back to dashboard")}</CloseButton>
          </EmbeddedHeader>
          <TrustStrip><LockOutlinedIcon /><span><strong>{L("Connexion bancaire sécurisée", "Secure bank connection")}</strong> — {L("Le Petit Boulot ne voit jamais votre IBAN complet et ne vous demandera pas de le saisir ailleurs.", "Le Petit Boulot never sees your full IBAN and will never ask you to enter it elsewhere.")}</span></TrustStrip>
          <StripeEmbeddedComponent component={embeddedComponent} onExit={closeEmbedded} />
        </EmbeddedArea>
      )}

      <FooterRow>
        <RefreshButton type="button" onClick={refresh} disabled={loadingStatus}>
          <RefreshOutlinedIcon /> {loadingStatus ? L('Vérification…', 'Checking…') : L('Actualiser le statut', 'Refresh status')}
        </RefreshButton>
        <small>{L("Le prestataire de paiement ne communique jamais votre IBAN complet à LePetitBoulot.", "The payment provider never shares your full IBAN with LePetitBoulot.")}</small>
      </FooterRow>

      {error && <Error role="alert"><strong>{L("Impossible de continuer.", "Unable to continue.")}</strong><span>{error}</span></Error>}
    </Card>
  );
};

const Card = styled.section`display:grid;gap:${({$expanded})=>$expanded?'.75rem':'1rem'};min-width:0;padding:${({$expanded})=>$expanded?'clamp(.8rem,1.8vw,1rem)':'clamp(.9rem,2vw,1.15rem)'};border:1px solid var(--color-border);border-radius:1rem;background:#fff;box-shadow:0 .45rem 1.4rem rgba(17,17,17,.035);`;
const Header = styled.div`display:grid;grid-template-columns:auto minmax(0,1fr) auto;align-items:center;gap:.7rem;min-width:0;h3{margin:0;font-size:1rem;}p{margin:.2rem 0 0;color:var(--color-textMuted);font-size:.75rem;line-height:1.5;}@media(max-width:42rem){grid-template-columns:auto minmax(0,1fr);}`;
const Icon = styled.span`width:2.7rem;height:2.7rem;display:grid;place-items:center;border-radius:.8rem;background:#111;color:#fff;`;
const State = styled.span`display:inline-flex;align-items:center;gap:.25rem;padding:.4rem .6rem;border-radius:999px;background:${({$ready})=>$ready?'#e8f7ee':'#fff4d6'};color:${({$ready})=>$ready?'#17623a':'#765209'};font-size:.68rem;font-weight:850;white-space:nowrap;svg{font-size:.9rem;}@media(max-width:42rem){grid-column:1/-1;justify-self:start;}`;
const Steps = styled.div`display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:.65rem;@media(max-width:48rem){grid-template-columns:1fr;}`;
const Step = styled.div`display:grid;grid-template-columns:auto minmax(0,1fr);align-items:start;gap:.55rem;padding:.75rem;border:1px solid ${({$done})=>$done?'#c9e9d4':'#e5dfd4'};border-radius:.85rem;background:${({$done})=>$done?'#f3fbf6':'#faf8f4'};color:${({$done})=>$done?'#17623a':'#4f4a43'};svg{font-size:1.15rem;}div{display:grid;gap:.12rem;}strong{font-size:.76rem;}span{color:#746e66;font-size:.68rem;line-height:1.4;}`;
const Info = styled.p`margin:0;color:#58534c;font-size:.76rem;line-height:1.55;`;
const SuccessMessage = styled.p`display:flex;align-items:flex-start;gap:.45rem;margin:0;padding:.75rem .85rem;border-radius:.8rem;background:#edf9f1;color:#17623a;font-size:.76rem;line-height:1.5;svg{flex:0 0 auto;font-size:1rem;margin-top:.1rem;}`;
const PrimaryButton = styled.button`justify-self:start;min-height:2.75rem;padding:.65rem 1rem;border:0;border-radius:999px;background:#111;color:#fff;font:inherit;font-size:.78rem;font-weight:850;cursor:pointer;&:disabled{opacity:.55;cursor:wait;}@media(max-width:34rem){width:100%;}`;
const UpgradeNotice = styled.div`display:flex;align-items:center;justify-content:space-between;gap:.85rem;padding:.85rem;border:1px solid #e7d3a7;border-radius:.85rem;background:#fff9ec;color:#5f491e;div{display:grid;gap:.2rem;min-width:0;}strong{font-size:.78rem;}span{font-size:.7rem;line-height:1.5;}@media(max-width:42rem){align-items:stretch;flex-direction:column;}`;
const UpgradeButton = styled.button`display:inline-flex;align-items:center;justify-content:center;gap:.35rem;flex:0 0 auto;min-height:2.55rem;padding:.55rem .8rem;border:0;border-radius:999px;background:#111;color:#fff;font:inherit;font-size:.7rem;font-weight:850;cursor:pointer;svg{font-size:1rem;}&:disabled{opacity:.55;cursor:wait;}`;
const Menu = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.65rem;@media(max-width:40rem){grid-template-columns:1fr;}`;
const MenuButton = styled.button`display:flex;align-items:center;gap:.55rem;min-width:0;min-height:3rem;padding:.75rem .85rem;border:1px solid #e5dfd4;border-radius:.85rem;background:#faf8f4;color:#111;font:inherit;font-size:.75rem;font-weight:800;text-align:left;cursor:pointer;svg{font-size:1.05rem;flex:0 0 auto;}&:hover{border-color:#bda16f;background:#fffaf0;}`;
const EmbeddedArea = styled.div`display:grid;gap:.7rem;min-width:0;width:100%;`;
const EmbeddedHeader = styled.div`display:flex;align-items:flex-start;justify-content:space-between;gap:.75rem;padding:.1rem .15rem;div{display:grid;gap:.18rem;min-width:0;}strong{font-size:.88rem;}span{color:#746e66;font-size:.72rem;line-height:1.45;}@media(max-width:34rem){flex-direction:column;}`;
const CloseButton = styled.button`display:inline-flex;align-items:center;justify-content:center;gap:.3rem;min-height:2.55rem;padding:.48rem .75rem;border:1px solid #ded6ca;border-radius:999px;background:#fff;color:#111;font:inherit;font-size:.68rem;font-weight:800;cursor:pointer;svg{font-size:.95rem;}@media(max-width:34rem){width:100%;}`;
const TrustStrip = styled.div`display:flex;align-items:flex-start;gap:.45rem;padding:.62rem .72rem;border:1px solid #d8e8de;border-radius:.75rem;background:#f1faf4;color:#285f3f;font-size:.66rem;line-height:1.45;svg{flex:0 0 auto;font-size:1rem;margin-top:.05rem;}strong{font-weight:850;}`;
const FooterRow = styled.div`display:flex;align-items:center;justify-content:space-between;gap:.75rem;small{color:#777067;font-size:.66rem;line-height:1.4;}@media(max-width:38rem){align-items:flex-start;flex-direction:column;}`;
const RefreshButton = styled.button`display:inline-flex;align-items:center;gap:.35rem;padding:0;border:0;background:transparent;color:#6f531d;font:inherit;font-size:.7rem;font-weight:800;cursor:pointer;svg{font-size:.95rem;}&:disabled{opacity:.55;cursor:wait;}`;
const Error = styled.p`display:grid;gap:.15rem;margin:0;padding:.75rem .85rem;border:1px solid #efcfcb;border-radius:.8rem;background:#fff6f5;color:#8c332c;font-size:.72rem;line-height:1.45;`;

export default StripeConnectPanel;
